import { destinationConnexion } from "./navigation.ts";
import type { Catalogue, ContexteSession, Entite, Famille, Reservation, Role, TotauxReservation, TypeEntite } from "@hopitech/contracts";

/**
 * Client de l'API Hopitech. Toute mutation obtient d'abord un jeton anti-rejeu
 * (contrat PortEntree) puis l'envoie dans l'en-tête. La session vit dans un cookie
 * HttpOnly posé par le serveur ; le SPA ne la lit jamais.
 */

export class ErreurApi extends Error {
  readonly statut: number;
  readonly code: string;
  constructor(statut: number, code: string, message: string) {
    super(message);
    this.statut = statut;
    this.code = code;
  }
}

async function lireErreur(r: Response): Promise<ErreurApi> {
  let code = "erreur";
  let message = `HTTP ${r.status}`;
  try {
    const j = (await r.json()) as { code?: string; message?: string };
    code = j.code ?? code;
    message = j.message ?? message;
  } catch {
    /* corps non JSON */
  }
  return new ErreurApi(r.status, code, message);
}

export async function apiGet<T>(chemin: string): Promise<T> {
  const r = await fetch(chemin, { headers: { accept: "application/json" } });
  if (!r.ok) throw await lireErreur(r);
  return (await r.json()) as T;
}

async function jetonAntiRejeu(): Promise<string> {
  const r = await fetch("/api/session/jeton", { headers: { accept: "application/json" } });
  if (!r.ok) throw await lireErreur(r);
  return ((await r.json()) as { jeton: string }).jeton;
}

export async function apiMutation<T>(
  methode: "POST" | "PUT" | "DELETE" | "PATCH",
  chemin: string,
  corps?: unknown,
): Promise<T> {
  const jeton = await jetonAntiRejeu();
  const r = await fetch(chemin, {
    method: methode,
    headers: {
      accept: "application/json",
      "x-jeton-anti-rejeu": jeton,
      ...(corps !== undefined ? { "content-type": "application/json" } : {}),
    },
    ...(corps !== undefined ? { body: JSON.stringify(corps) } : {}),
  });
  if (!r.ok) throw await lireErreur(r);
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

// --- Santé & contexte ---

export function sante(): Promise<{ authentification: "better-auth" | "stub" }> {
  return apiGet("/api/sante");
}

export function moi(): Promise<ContexteSession> {
  return apiGet("/api/moi");
}

// --- Authentification Better Auth (mode réel) ---

async function authPost(chemin: string, corps: unknown): Promise<void> {
  const r = await fetch(`/api/auth${chemin}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(corps),
  });
  if (!r.ok) throw await lireErreur(r);
}

export const connexionEmail = (email: string, password: string): Promise<void> => authPost("/sign-in/email", { email, password });
export const inscriptionEmail = (email: string, password: string, name: string): Promise<void> => authPost("/sign-up/email", { email, password, name });
export const lienMagique = (email: string, callbackURL = "/"): Promise<void> => authPost("/sign-in/magic-link", { email, callbackURL: new URL(destinationConnexion(callbackURL), window.location.origin).href });
export const deconnexionEmail = (): Promise<void> => authPost("/sign-out", {});

// Bascule de compte (impersonation admin de Better Auth).
export const impersonate = (userId: string): Promise<void> => authPost("/admin/impersonate-user", { userId });
export const stopImpersonate = (): Promise<void> => authPost("/admin/stop-impersonating", {});

// --- Administration des comptes & rattachements (rôle administrateur) ---

export interface CompteAnnuaire {
  id: string;
  email: string;
  nom: string;
  rattachements: { role: Role; edition: number }[];
}
export const adminComptes = (): Promise<{ comptes: CompteAnnuaire[] }> => apiGet("/api/admin/comptes");
export const ajouterRattachement = (email: string, role: Role, edition: number): Promise<void> => apiMutation("POST", "/api/admin/rattachements", { email, role, edition });
export const retirerRattachement = (email: string, role: Role, edition: number): Promise<void> => apiMutation("DELETE", "/api/admin/rattachements", { email, role, edition });

// --- Session de développement (mode stub uniquement) ---

export async function ouvrirSessionDev(params: { compte: string; edition: number; role: Role }): Promise<void> {
  const r = await fetch("/api/session/dev", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!r.ok) throw await lireErreur(r);
}

// --- Catalogue ---

export function lireCatalogue(edition: number, famille?: Famille): Promise<Catalogue> {
  const q = new URLSearchParams({ edition: String(edition) });
  if (famille) q.set("famille", famille);
  return apiGet<Catalogue>(`/api/catalogue?${q.toString()}`);
}

// --- Réservation exposant ---

export interface CreationReservation {
  reference: string;
  etat: string;
  version: number;
}
export interface ValidationReservation {
  reference: string;
  etat: string;
  valideeLe: string;
  cleIdempotence: string;
  version: number;
  totaux: TotauxReservation;
}

export function creerReservation(corps: {
  edition: number;
  enseigne: string;
  souhaitPlacement: { referenceStand: string; preferenceZone?: string };
}): Promise<CreationReservation> {
  return apiMutation("POST", "/api/reservations", corps);
}

export function ajouterLigne(
  reference: string,
  ligne: { referenceArticle: string; famille: Famille; quantite: number },
): Promise<{ reference: string; lignes: number; version: number }> {
  return apiMutation("POST", `/api/reservations/${encodeURIComponent(reference)}/lignes`, ligne);
}

export function validerReservation(reference: string): Promise<ValidationReservation> {
  return apiMutation("POST", `/api/reservations/${encodeURIComponent(reference)}/validation`);
}

export function lireReservation(reference: string): Promise<{ reservation: Reservation; totaux?: TotauxReservation }> {
  return apiGet(`/api/reservations/${encodeURIComponent(reference)}`);
}

// --- Entités de gestion (CRUD générique) ---

export function listerEntites(type: TypeEntite, edition: number): Promise<{ entites: Entite[] }> {
  return apiGet(`/api/entites/${type}?edition=${edition}`);
}
export function lireEntite(type: TypeEntite, id: string): Promise<Entite> {
  return apiGet(`/api/entites/${type}/${encodeURIComponent(id)}`);
}
export function creerEntite(type: TypeEntite, edition: number, donnees: Record<string, unknown>): Promise<Entite> {
  return apiMutation("POST", `/api/entites/${type}`, { edition, donnees });
}
export function modifierEntite(type: TypeEntite, id: string, donnees: Record<string, unknown>, version: number): Promise<Entite> {
  return apiMutation("PUT", `/api/entites/${type}/${encodeURIComponent(id)}`, { donnees, version });
}
export function supprimerEntite(type: TypeEntite, id: string): Promise<{ ok: boolean }> {
  return apiMutation("DELETE", `/api/entites/${type}/${encodeURIComponent(id)}`);
}
export function statsEdition(edition: number): Promise<{ comptes: Record<string, number> }> {
  return apiGet(`/api/stats/${edition}`);
}
export function mesEntites(type: TypeEntite, edition: number): Promise<{ entites: Entite[] }> {
  return apiGet(`/api/mes-entites/${type}?edition=${edition}`);
}
export function jeuQuestions(edition: number): Promise<{ entites: Entite[] }> {
  return apiGet(`/api/jeu/${edition}/questions`);
}

// --- Inscription publique (sans compte) ---

export async function inscriptionPublique(type: TypeEntite, edition: number, donnees: Record<string, unknown>): Promise<void> {
  const r = await fetch(`/api/public/inscription/${type}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ edition, donnees }),
  });
  if (!r.ok) throw await lireErreur(r);
}
